import { walkTree } from '../../../components/Tree/helpers/helpers';
import { ReadOnlyArray } from '../../../utils/array/ReadOnlyArray';
import { CATALOG_STATUS_ACTIVE } from '../../../utils/statuses/catalogStatus';
import {
  CATALOG_TYPE_MASTER,
  CATALOG_TYPE_UNCATEGORIZED,
  CATALOG_TYPE_THEMES,
  CATALOG_TYPE_ITEMS_CATEGORY_RECOMMENDATIONS,
} from '../../../utils/types/catalogTypes';
import { selectPanes } from '../pane/data';
import { selectTreeDataByCatalogId } from '../tree/tree';
import { createReferenceEqualSelector } from '../utils';

const filterLeafCategories = (treeData) => {
  const categories = new Map();
  walkTree({
    treeData,
    callback: (ni) => {
      const { category } = ni.node;
      if (category?.categoryId && category.childCategoryCount === 0) {
        categories.set(category.externalCategoryId, category);
      }
    },
    ignoreCollapsed: false,
  });
  return categories;
};

// Factory function to create catalog ID selector for single or multiple catalog types
const createCatalogIdSelector = (primaryType, requiredTypes = []) => {
  return createReferenceEqualSelector(selectPanes, (panes) => {
    const required = Array.isArray(requiredTypes) ? requiredTypes : [requiredTypes];
    const result = {
      catalogId: null,
      foundPrimary: false,
      foundRequired: new Set(),
    };

    panes.forEach((pane) => {
      if (pane.catalog.catalogStatus === CATALOG_STATUS_ACTIVE) {
        if (pane.catalog.catalogType === primaryType) {
          result.catalogId = pane.catalog.catalogId;
          result.foundPrimary = true;
        }
        if (required.includes(pane.catalog.catalogType)) {
          result.foundRequired.add(pane.catalog.catalogType);
        }
      }
    });

    // If required types specified, only return catalogId if primary and all required are found
    if (required.length > 0) {
      return result.foundPrimary && result.foundRequired.size === required.length
        ? result.catalogId
        : null;
    }
    return result.catalogId;
  });
};

// Factory function to create tree data selector
const createTreeDataSelector = (catalogIdSelector) => {
  return createReferenceEqualSelector(
    (state) => state,
    catalogIdSelector,
    (state, catalogId) => {
      if (!catalogId) {
        return ReadOnlyArray.Empty;
      }
      return selectTreeDataByCatalogId(state, catalogId);
    },
  );
};

// Factory function to create categories selector
const createCategoriesSelector = (treeDataSelector) => {
  return createReferenceEqualSelector(treeDataSelector, filterLeafCategories);
};

// Master catalog + Uncategorized (requires both)
const catalogIdForPredictions = createCatalogIdSelector(CATALOG_TYPE_MASTER, [
  CATALOG_TYPE_UNCATEGORIZED,
]);
export const selectCatalogIdForPredictions = catalogIdForPredictions;
export const selectTreeDataForPredictions = createTreeDataSelector(
  catalogIdForPredictions,
);
export const selectCategoriesForPredictions = createCategoriesSelector(
  selectTreeDataForPredictions,
);

// Themes + Item Recommendations (requires both)
const catalogIdForThemesAndItemRecommendations = createCatalogIdSelector(
  CATALOG_TYPE_THEMES,
  [CATALOG_TYPE_ITEMS_CATEGORY_RECOMMENDATIONS],
);
export const selectCatalogIdForThemesAndItemRecommendations =
  catalogIdForThemesAndItemRecommendations;
export const selectTreeDataForThemesAndItemRecommendations = createTreeDataSelector(
  catalogIdForThemesAndItemRecommendations,
);
export const selectCategoriesForThemesAndItemRecommendations =
  createCategoriesSelector(selectTreeDataForThemesAndItemRecommendations);
